import React, { useState, useEffect, useRef, useMemo } from "react";
import "./index.less";
import { Input } from "antd";
import { SearchOutlined, CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { elementDesc } from "@/config/dataMap";
import { typeMap3, typeMap4 } from "@/constants/dataMap";
import ResultComponent from "./resultComponent";

interface calculateTask3DYCZProps {
    input: any;
    transInput: any;
    results: any;
    job_id: number;
    type: string;
    subJobid: number;
    item_name: string;
    streamDescMap?: Record<string, string>;
    streamNodeMap?: Record<string, { fromName: string; toName: string }>;
    elementDescMap?: Record<string, string>;
    tempUnit?: string;
    pressUnit?: string;
    setTempUnit?: (v: string) => void;
    setPressUnit?: (v: string) => void;
    onElementChange?: (name: string) => void;
    activeElementName?: string;
    compareItems?: any[];
}
//元件菜单高亮备注
// ==================== 对比辅助函数（元件树高亮用） ====================
// 判断两个值是否有差异（缺失值处理与表格对比逻辑一致）
const valuesDiffer = (current: any, compare: any): boolean => {
    const curMissing = current === "-" || current === null || current === undefined || current === "";
    const cmpMissing = compare === "-" || compare === null || compare === undefined || compare === "";
    // 任一侧缺失（页面显示为 "-"）都不算差异，不标红
    if (curMissing || cmpMissing) return false;
    const curNum = parseFloat(String(current));
    const cmpNum = parseFloat(String(compare));
    if (!isNaN(curNum) && !isNaN(cmpNum)) {
        // 数值比较
        // return curNum !== cmpNum;
        // 数值比较：相对容差，避免浮点运算精度噪声（如组分浓度乘除顺序不同产生的末位差异）导致误判
        if (curNum === cmpNum) return false;
        const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
        return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return String(current) !== String(compare);
};

// 对比流股压力：按 p_unit 换算为 Pa（与塔表格 parseCompareStreamData 一致）
const parseComparePressure = (cmp: any): number | null => {
    const p = cmp?.params?.p;
    if (p === null || p === undefined) return null;
    const unit = cmp?.params?.p_unit || "MPa";
    switch (unit) {
        case "Pa": return Number(p);
        case "bar": return Number(p) * 100000;
        case "KPa": return Number(p) * 1000;
        case "MPa": return Number(p) * 1000000;
        case "atm": return Number(p) * 101325;
        default: return Number(p) * 1000000;
    }
};

// 压力显示单位对应的 Pa 倍率（与塔表格压力换算一致）
const getPressureScale = (unit?: string): number => {
    switch (unit) {
        case "Pa": return 1;
        case "bar": return 100000;
        case "KPa": return 1000;
        case "atm": return 101325;
        case "MPa":
        default: return 1000000;
    }
};

// 在对比项中查找与流股同名的对比流股数据（与各表格一致：优先 fullOutput，其次 mix_input）
// isTower=true 时（塔表格）额外回退 values.streams；非塔表格只用 fullOutput/mix_input
const findCompareStream = (compareItem: any, streamName: string, isTower = false): any =>
    compareItem?.values?.fullOutput?.streams?.find((s: any) => s.name === streamName)
    || compareItem?.values?.mix_input?.streams?.find((s: any) => s.name === streamName)
    || (isTower ? compareItem?.values?.streams?.find((s: any) => s.name === streamName) : undefined);

// 组分表 5 种显示模式
const COMPONENT_MODES = ["molarFraction", "massFraction", "massFlow", "molarFlow", "massConcentration"];

// 计算某条流股第 i 个组分的"计算值"（复刻 StreamResult/塔组分表 calc 侧：缺失按 0，与表格 `|| 0` 一致）
const calcComponentValue = (stream: any, i: number, mode: string): number => {
    const massFrac = stream.mass_frac?.[i] || 0;
    const molarFrac = stream.molar_frac?.[i] || 0;
    const fKgPerH = stream.f_kg_per_h || 0;
    const fMolPerH = stream.f_mol_per_h || 0;
    const fNm3PerH = stream.f_nm3_per_h || stream.f_m3_per_h || 1;
    switch (mode) {
        case "massFraction": return massFrac;
        case "massFlow": return fKgPerH * massFrac;
        case "molarFlow": return (fMolPerH * molarFrac) / 1000;
        case "massConcentration": return (fKgPerH * 1e6 * massFrac) / fNm3PerH;
        case "molarFraction":
        default: return molarFrac;
    }
};

// 计算某条流股第 i 个组分的"对比值"（复刻组分表 compare 侧；返回 null 表示表格显示"-"）
// isTower：塔组分表摩尔流量直接用 f_mol_per_h；非塔（StreamResult）用 params.f ÷ 平均分子量
const calcCompareComponentValue = (cmp: any, i: number, mode: string, mats: any[], isTower: boolean): number | null => {
    const cmpMat = cmp?.materials?.[i];
    const cmpMassFracArr = cmp?.mass_frac;
    const cmpMassFrac = cmpMassFracArr?.[i];
    switch (mode) {
        case "molarFraction": {
            // 摩尔分率缺失时显示 "-"
            return cmpMat?.molar_frac ?? null;
        }
        case "massFraction": {
            if (!cmpMassFracArr || cmpMassFrac == null) return null;
            return cmpMassFrac;
        }
        case "massFlow": {
            if (!cmpMassFracArr || cmpMassFrac == null) return null;
            return (cmp?.params?.f || 0) * cmpMassFrac;
        }
        case "molarFlow": {
            if (isTower) {
                // 塔组分表：缺失时按 0（null×数值=0，与表格一致）
                return ((cmp?.f_mol_per_h ?? 0) / 1000) * (cmpMat?.molar_frac ?? 0);
            }
            // StreamResult 组分表：摩尔分率缺失时显示 "-"（视为差异）
            const mf = cmpMat?.molar_frac ?? null;
            if (mf == null) return null;
            const avgMW = (cmp?.materials || []).reduce((sum: number, m: any, j: number) => {
                return sum + (m?.molar_frac || 0) * (mats[j]?.MW || 1);
            }, 0);
            const totalMolPerH = avgMW ? (cmp?.params?.f || 0) / avgMW : 0;
            return totalMolPerH * mf;
        }
        case "massConcentration": {
            if (!cmpMassFracArr || cmpMassFrac == null) return null;
            const volFlow = cmp?.f_nm3_per_h || cmp?.f_m3_per_h || 1;
            return ((cmp?.params?.f || 0) * cmpMassFrac * 1e6) / volFlow;
        }
        default: return null;
    }
};

// 收集单条流股的工艺参数/组分数据差异字段（返回差异描述数组；判定逻辑与各表格标红保持一致）
const collectStreamDiffFields = (main: any, cmp: any, opts: { isTower?: boolean; mats?: any[]; tempUnit?: string; pressUnit?: string }): string[] => {
    const diffs: string[] = [];
    const isTower = !!opts?.isTower;
    const tempUnit = opts?.tempUnit === "K" ? "K" : "℃";
    const pressUnit = opts?.pressUnit || "MPa";
    const push = (desc: string) => { if (!diffs.includes(desc)) diffs.push(desc); };

    // ===== 温度（与表格显示/标红判定一致） =====
    // 塔表格：按显示单位保留 2 位小数（getCalcVal/getCompareVal）；非塔表格：显示单位下原始值比较
    if (isTower) {
        if (tempUnit === "K") {
            const tc = main.T_K != null ? Number(main.T_K.toFixed(2)) : null;
            const cc = cmp?.params?.t != null ? Number((Number(cmp.params.t) + 273.15).toFixed(2)) : null;
            if (valuesDiffer(tc, cc)) push(`温度(${tc} vs ${cc} K)`);
        } else {
            // ℃：主侧 T_K-273.15（与塔表格一致，不读 T_C），对比侧 params.t
            const tc = main.T_K != null ? Number((main.T_K - 273.15).toFixed(2)) : null;
            const cc = cmp?.params?.t != null ? Number(Number(cmp.params.t).toFixed(2)) : null;
            if (valuesDiffer(tc, cc)) push(`温度(${tc} vs ${cc} ℃)`);
        }
    } else {
        const tCalc = tempUnit === "K" ? main.T_K : main.T_C;
        const tRaw = cmp?.params?.t != null ? Number(cmp.params.t) : null;
        const tCmp = tRaw == null ? null : (tempUnit === "K"
            ? (cmp?.params?.t_unit === "K" ? tRaw : tRaw + 273.15)
            : (cmp?.params?.t_unit === "C" ? tRaw : tRaw - 273.15));
        if (valuesDiffer(tCalc, tCmp)) push(`温度(${tCalc} vs ${tCmp})`);
    }

    // ===== 压力（与表格显示/标红判定一致） =====
    if (isTower) {
        // 塔表格：按显示单位保留 2 位小数（effectiveSelectP = "P_" + pressUnit）
        const scale = getPressureScale(pressUnit);
        const mainP = main.P_Pa != null ? Number((main.P_Pa / scale).toFixed(2)) : null;
        const cmpPa = parseComparePressure(cmp);
        const cmpP = cmpPa != null ? Number((cmpPa / scale).toFixed(2)) : null;
        if (valuesDiffer(mainP, cmpP)) push(`压力(${mainP} vs ${cmpP} ${pressUnit})`);
    } else {
        // 非塔表格：按显示单位原始值比较（默认 MPa：main.P_MPa vs params.p），避免换算浮点噪声
        const calcP = main[`P_${pressUnit}`];
        const pRaw = cmp?.params?.p != null ? Number(cmp.params.p) : null;
        let cmpP: any = pRaw;
        if (pRaw != null) {
            if (pressUnit === "Pa") cmpP = pRaw * 1e6;
            else if (pressUnit === "KPa") cmpP = pRaw * 1000;
            else if (pressUnit === "bar") cmpP = pRaw * 10;
            else if (pressUnit === "atm") cmpP = pRaw / 0.101325;
        }
        if (valuesDiffer(calcP ?? 0, cmpP)) push(`压力(${calcP} vs ${cmpP} ${pressUnit})`);
    }

    // ===== 摩尔流量 kmol/h（与表格一致：对比侧 f_mol_per_h > 0 才取值，否则显示"-"） =====
    if (isTower) {
        const calcMol = (main.f_mol_per_h != null ? main.f_mol_per_h / 1000 : null) || "-";
        const cmpMol = cmp?.f_mol_per_h > 0 ? cmp.f_mol_per_h / 1000 : "-";
        if (valuesDiffer(calcMol, cmpMol)) push(`摩尔流量(${calcMol} vs ${cmpMol} kmol/h)`);
    } else {
        const calcMol = main.f_mol_per_h != null ? main.f_mol_per_h / 1000 : null;
        const cmpMol = cmp?.f_mol_per_h > 0 ? cmp.f_mol_per_h / 1000 : null;
        if (valuesDiffer(calcMol, cmpMol)) push(`摩尔流量(${calcMol} vs ${cmpMol} kmol/h)`);
    }
    // 质量流量 kg/h
    if (valuesDiffer(main.f_kg_per_h, cmp?.params?.f)) push(`质量流量(${main.f_kg_per_h} vs ${cmp?.params?.f} kg/h)`);
    // 气化分率
    if (valuesDiffer(main.vf, cmp?.vf)) push(`气化分率(${main.vf} vs ${cmp?.vf})`);
    // 焓值
    if (valuesDiffer(main.H, cmp?.H)) push(`焓值(${main.H} vs ${cmp?.H})`);
    // 气相体积流量 Nm³/h
    if (valuesDiffer(main.f_nm3_per_h, cmp?.f_nm3_per_h)) push(`气相体积流量(${main.f_nm3_per_h} vs ${cmp?.f_nm3_per_h} Nm³/h)`);
    // 液相体积流量：与表格默认视图一致，只比较 m³/h（表格默认选择 m³/h，L/h 需手动切换）
    if (valuesDiffer(main.f_m3_per_h, cmp?.f_m3_per_h)) push(`液相体积流量(${main.f_m3_per_h} vs ${cmp?.f_m3_per_h} m³/h)`);

    // ===== 组分数据（显示切换的 5 种类型都要比较） =====
    const mats = opts?.mats || [];
    // 比较全部分量（长度取主/对比组分数组的最大值，超出部分按缺失处理，与表格"缺失显示为-"一致）
    const maxLen = Math.max(
        Array.isArray(main.molar_frac) ? main.molar_frac.length : 0,
        Array.isArray(main.mass_frac) ? main.mass_frac.length : 0,
        Array.isArray(cmp?.materials) ? cmp.materials.length : 0,
        Array.isArray(cmp?.mass_frac) ? cmp.mass_frac.length : 0
    );
    const indices = maxLen > 0 ? Array.from({ length: maxLen }, (_, i) => i) : [];
    for (const i of indices) {
        for (const mode of COMPONENT_MODES) {
            const calcV = calcComponentValue(main, i, mode);
            const cmpV = calcCompareComponentValue(cmp, i, mode, mats, isTower);
            // 与表格一致：计算值缺失按 0；对比值缺失(null)视为差异（表格显示"-"）
            if (valuesDiffer(calcV, cmpV)) push(`组分[${i}](${mode})(${calcV} vs ${cmpV})`);
        }
    }
    return diffs;
};

// 判断单条流股的工艺参数/组分数据是否有差异（与各表格标红判定保持一致）
const compareStreamFields = (main: any, cmp: any, opts: { isTower?: boolean; mats?: any[]; tempUnit?: string; pressUnit?: string }): boolean =>
    collectStreamDiffFields(main, cmp, opts).length > 0;

export default function CalculateTask3DYCZ({
    subJobid,
    item_name,
    input,
    transInput,
    results,
    job_id,
    type,
    streamDescMap,
    streamNodeMap,
    elementDescMap,
    tempUnit,
    pressUnit,
    setTempUnit,
    setPressUnit,
    onElementChange,
    activeElementName,
    compareItems = [],
}: calculateTask3DYCZProps) {
    const [selectTreeButton, setSelectTreeButton] = useState<any>({});
    const [treeButtons, setTreeButtons] = useState<any[]>([]);
    // 元件名筛选（侧边栏顶部搜索框）
    const [elementSearch, setElementSearch] = useState<string>("");
    // 元件分类折叠状态（按 type 分组，key 为 type）
    const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
    const scrollViewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const analyInputData = () => {
            if (!results) return;
            if (results.elements?.length) {
                // 用来存 { label: 显示名, name: 设备名, type: 设备类型 }
                const buttonList = [];

                // 1. 先处理 input 里的 DistilStrict
                const distilItems = input?.inputs?.elements?.filter((item: any) => item?.type === "DistilStrict" && input?.inputs.selected_blocks.includes(item?.name)) || [];
                distilItems.forEach((item: any) => {
                    const label = `${item.name} (${elementDesc[item.name] || ''})`;
                    buttonList.push({
                        label: label,        // 页面显示用
                        name: item.name,     // 真实设备名
                        type: item.type      // 你要的 type 
                    });
                });

                // 2. 合并 results 里的设备
                results.elements.forEach((item: any) => {
                    const label = `${item.name} (${elementDesc[item.name] || ''})`;
                    // 去重判断
                    const exist = buttonList.some(b => b.label === label);
                    if (!exist) {
                        buttonList.push({
                            label: label,
                            name: item.name,
                            type: item.type
                        });
                    }
                });

                setTreeButtons(buttonList);
                // 4. 默认选中：优先恢复父组件记录的已选元件（从流股页跳回时不刷新选中），否则默认第一个
                if (buttonList.length > 0) {
                    const saved = activeElementName
                        ? buttonList.find((b: any) => b.name === activeElementName)
                        : undefined;
                    const target = saved || buttonList[0];
                    setSelectTreeButton(target);
                    // 上报当前选中元件，供流股页默认选中该元件的输入输出流股
                    onElementChange?.(target.name);
                }
            }
        };

        analyInputData();
    }, [results, input]);
    //元件菜单高亮备注

    // 元件输入输出流股名称映射
    const elementStreamMap = useMemo(() => {
        const map: Record<string, { in: string[]; out: string[]; type?: string }> = {};
        input?.inputs?.elements?.forEach((unit: any) => {
            if (!unit?.name) return;
            map[unit.name] = {
                in: unit.inputStreams?.map((s: any) => s.name) || [],
                out: unit.outputStreams?.map((s: any) => s.name) || [],
                type: unit.type,
            };
        });
        return map;
    }, [input]);

    // 打印对比有差异的元件的差异项（有对比项时，遍历所有元件的输入输出流股，输出差异字段到控制台）
    useEffect(() => {
        if (!compareItems.length) return;
        const streams = results?.streams || [];
        const mats = (transInput as any)?.materials;
        treeButtons.forEach((btn: any) => {
            const unit = elementStreamMap[btn.name];
            if (!unit) return;
            const isTower = unit.type === "DistilStrict";
            const streamNames = [...unit.in, ...unit.out];
            streamNames.forEach((streamName) => {
                const main = streams.find((s: any) => s.name === streamName);
                if (!main) return;
                compareItems.forEach((item: any) => {
                    const cmp = findCompareStream(item, streamName, isTower);
                    const itemLabel = item?.values?.name ?? item.id;
                    if (!cmp) {
                        // 对比项中没有该流股数据时，表格会将对比列显示为"-"，视为有差异
                        console.log(`[对比差异] 元件 ${btn.name} 流股 ${streamName} 对比项 ${itemLabel}：对比数据缺失（表格显示"-"）`);
                        return;
                    }
                    const diffFields = collectStreamDiffFields(main, cmp, { isTower, mats, tempUnit, pressUnit });
                    if (diffFields.length) {
                        console.log(`[对比差异] 元件 ${btn.name} 流股 ${streamName} 对比项 ${itemLabel} 差异字段：${diffFields.join("、")}`);
                    }
                });
            });
        });
    }, [compareItems, treeButtons, elementStreamMap, results, transInput, tempUnit, pressUnit]);

    // 判断元件是否有输入输出对比差异（有对比项且任一流股任一对比项有差异则高亮）
    const elementHasDiff = (elementName: string): boolean => {
        if (!compareItems.length) return false;
        const unit = elementStreamMap[elementName];
        if (!unit) return false;
        const streamNames = [...unit.in, ...unit.out];
        if (!streamNames.length) return false;
        const streams = results?.streams || [];
        // 塔元件走塔表格公式；非塔元件走 StreamResult 组分表公式（摩尔流量换算不同）
        const isTower = unit.type === "DistilStrict";
        const mats = (transInput as any)?.materials;
        for (const streamName of streamNames) {
            const main = streams.find((s: any) => s.name === streamName);
            if (!main) continue;
            for (const item of compareItems) {
                const cmp = findCompareStream(item, streamName, isTower);
                // 对比项中没有该流股数据时表格对比列显示为 "-"，不算差异，跳过该对比项
                if (!cmp) continue;
                if (compareStreamFields(main, cmp, { isTower, mats, tempUnit, pressUnit })) return true;
            }
        }
        return false;
    };

    // 元件筛选后的左侧菜单项：按元件名/中文描述匹配（为空时显示全部）
    const filteredTreeButtons = useMemo(() => {
        const kw = elementSearch.trim().toLowerCase();
        if (!kw) return treeButtons;
        return treeButtons.filter(
            (b: any) =>
                String(b.name ?? "").toLowerCase().includes(kw) ||
                String(b.label ?? "").toLowerCase().includes(kw)
        );
    }, [treeButtons, elementSearch]);

    // 按元件类型分组（分组内保留搜索过滤结果）
    const groupedTreeButtons = useMemo(() => {
        const groups: Record<string, any[]> = {};
        filteredTreeButtons.forEach((item: any) => {
            const type = item.type || "其他";
            if (!groups[type]) groups[type] = [];
            groups[type].push(item);
        });
        return groups;
    }, [filteredTreeButtons]);

    // 类型中文名
    const getTypeNameCN = (type: string) => typeMap3[type] || typeMap4[type] || type;

    // 当前选中元件所属分类
    const currentElementType = useMemo(() => {
        if (!selectTreeButton?.name) return null;
        const el = treeButtons.find((b: any) => b.name === selectTreeButton.name);
        return el ? (el.type || "其他") : null;
    }, [selectTreeButton, treeButtons]);

    // 选中元件变化时自动展开其所属分类
    useEffect(() => {
        if (currentElementType) {
            setExpandedTypes((prev) => ({ ...prev, [currentElementType]: true }));
        }
    }, [currentElementType]);

    // 切换分类展开/折叠状态
    const toggleType = (type: string) => {
        setExpandedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    // 渲染侧边栏按钮列表 (PC 端默认显示)
    const renderItem1 = (item: any) => (
        <div className="tree-button-item" key={item.name}>
            <button
                onClick={() => {
                    setSelectTreeButton(item)
                    // 上报当前选中元件，供流股页默认选中该元件的输入输出流股
                    onElementChange?.(item.name)
                }
                }
                //  className={`tree-button ${selectTreeButton.name === item.name ? "active" : ""
                //     }`}
                    //元件菜单高亮备注
                className={`tree-button ${selectTreeButton.name === item.name ? "active" : ""
                    } ${compareItems.length > 0 && elementHasDiff(item.name) ? "has-diff" : ""}`}
            >
                {/* 设备名（中文名）备份 */}
                {/* <span className="tree-button-text">{item.label}</span> */}
                <span className="tree-button-text">{item.name}</span>
            </button>
        </div>
    );


    // 空数据判断
    if ((!input || !results || !transInput) && selectTreeButton === "" && treeButtons.length === 0) {
        return null;
    }

    return (
        <div className="calculate-task-container">
            {/* PC 端固定显示侧边栏按钮列表 */}
            {type!=='subJob' && (
                <div className="sidebar">
                    {/* 元件筛选：按元件名/中文描述过滤左侧元件菜单 */}
                    <div className="sidebar-filter">
                        <Input
                            size="small"
                            allowClear
                            prefix={<SearchOutlined style={{ color: "#999" }} />}
                            placeholder="筛选元件名称"
                            value={elementSearch}
                            onChange={(e) => setElementSearch(e.target.value)}
                        />
                    </div>
                    <div className="sidebar-scroll">
                        {filteredTreeButtons.length === 0 ? (
                            <div className="sidebar-empty">无匹配元件</div>
                        ) : (
                            Object.entries(groupedTreeButtons).map(([type, typeElements]) => {
                                const isExpanded =
                                    expandedTypes[type] ??
                                    (!!selectTreeButton?.name && type === currentElementType); // 只有在选中元件且是当前分类时才默认展开
                                const isCurrentType = type === currentElementType;
                                return (
                                    <div key={type} className="element-type-group">
                                        <div
                                            className="element-type-title"
                                            onClick={() => toggleType(type)}
                                            style={{
                                                cursor: "pointer",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                backgroundColor: isCurrentType ? "#e6f7ff" : "#f5f5f5",
                                                color: isCurrentType ? "#165DFF" : "#666",
                                                fontSize: 14,
                                                fontWeight: 590,
                                            }}
                                        >
                                            <span>{getTypeNameCN(type)} ({typeElements.length})</span>
                                            {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                        </div>
                                        {isExpanded && (
                                            <div className="element-type-items">
                                                {typeElements.map(renderItem1)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
            {/* 主内容区域 */}
            <div
                className="main-content"
                ref={scrollViewRef}
            >
                {/* 结果组件区域 */}
                <div className="result-content">
                    <ResultComponent
                        scrollViewRef={scrollViewRef}
                        input={input}
                        transInput={transInput}
                        results={results}
                        selectTreeButton={selectTreeButton}
                        subJobid={subJobid}
                        item_name={item_name}
                        job_id={job_id}
                        type={type}
                        streamDescMap={streamDescMap}
                        streamNodeMap={streamNodeMap}
                        elementDescMap={elementDescMap}
                        tempUnit={tempUnit}
                        pressUnit={pressUnit}
                        setTempUnit={setTempUnit}
                        setPressUnit={setPressUnit}
                    />
                </div>
            </div>

        </div>
    );
}